import { Box, Button, Dialog, Typography } from '@mui/material';
import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { deleteCustomList } from 'entities/CustomList';
import { getSelectedProperty } from 'entities/Property/model/selectors/PropertySelectors';
import { getRouteCatalog } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { Loader } from 'shared/ui/Loader/Loader';

interface DeleteCustomListDialogProps {
    open: boolean;
    listId: string;
    listName: string;
    propertyId: string;
    onClose: () => void;
}

export const DeleteCustomListDialog = memo(
    ({
        open,
        listId,
        listName,
        propertyId,
        onClose,
    }: DeleteCustomListDialogProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const navigate = useNavigate();
        const property = useSelector(getSelectedProperty);
        const [loading, setLoading] = useState(false);

        const propertyName =
            property?.propertyNameLegal ||
            property?.propertyNameMarketing ||
            '';

        if (!open) {
            return null;
        }

        const onSubmit = async () => {
            setLoading(true);
            const result = await dispatch(deleteCustomList({ listId }));
            setLoading(false);

            if (result.meta.requestStatus === 'fulfilled') {
                toast(t('customLists.listDeleted', { name: listName }));
                onClose();
                navigate(getRouteCatalog(propertyId));
            } else {
                toast.error(t('customLists.failedToDeleteList'));
            }
        };

        return (
            <Dialog open onClose={onClose}>
                <DialogBoxContent
                    onClose={onClose}
                    buttons={
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            width="100%"
                            gap="8px"
                        >
                            <Button
                                onClick={onClose}
                                data-testclass="delete-custom-list-cancel"
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={loading}
                                startIcon={
                                    loading ? (
                                        <Loader color="inherit" />
                                    ) : undefined
                                }
                                onClick={onSubmit}
                                data-testclass="delete-custom-list-confirm"
                            >
                                {t('Delete')}
                            </Button>
                        </Box>
                    }
                >
                    <Box
                        sx={{
                            maxWidth: '400px',
                            paddingBottom: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px',
                            }}
                        >
                            {propertyName ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'rgba(0, 0, 0, 0.4)',
                                    }}
                                >
                                    {propertyName}
                                </Typography>
                            ) : null}
                            <Typography variant="h4">
                                {t('customLists.deleteList')}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                            }}
                        >
                            <Typography variant="body1">
                                <Trans
                                    i18nKey="customLists.deleteListConfirm"
                                    values={{ name: listName }}
                                    components={{
                                        bold: (
                                            <Box
                                                component="span"
                                                sx={{ fontWeight: 700 }}
                                            />
                                        ),
                                    }}
                                />
                            </Typography>
                            <Typography variant="body1">
                                {t('customLists.deleteListConfirmNote')}
                            </Typography>
                        </Box>
                    </Box>
                </DialogBoxContent>
            </Dialog>
        );
    },
);
